/**
 * Public "Reported Concerns" map/feed (public-concerns-map.md / ADR-0023) — GET /api/public/concerns,
 * no auth. Byte-exact mirror of handler/concerns_public.go's GeoJSON FeatureCollection. Filters
 * (category/status/from/to) are passed through as query params; the server does ALL privacy
 * enforcement (coordinate fuzzing + k-anonymity suppression below the tenant's MinCount) — this
 * client never re-derives or second-guesses either, it only maps the wire shape into
 * PublicConcernsView (types.ts).
 */
import { apiFetch } from "./client.js";
import type {
  PublicConcernPin,
  PublicConcernsFilter,
  PublicConcernsView,
  PublicConcernStatus,
} from "./types.js";

/** Raw wire shape of one GeoJSON Feature's `properties` (handler.PublicConcerns). */
interface WireConcernProperties {
  ref: string;
  category: string;
  /** Raw category id — see types.ts's PublicConcernPin.categoryId doc. Optional for backward
   *  compatibility with an older backend response shape. */
  categoryId?: string;
  /** Fixed-vocabulary issue type (see types.ts's PublicConcernPin.type doc) — empty/absent when
   *  not set server-side. */
  type?: string;
  status: string;
  /** PII-safe street-level label (e.g. "Oak St") — NO house number; empty/absent when no street
   *  could be safely extracted server-side (ADR-0023 two-tier location). */
  location?: string;
  dateReported: string;
  dateResolved: string | null;
}

interface WireConcernFeature {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: WireConcernProperties;
}

export interface WireConcernsResponse {
  type: "FeatureCollection";
  features: WireConcernFeature[];
  summary: { reported: number; resolved: number };
  /** Present+true only in the k-anonymity-suppressed shape (ADR-0023 decision 7) — below the
   *  tenant's MinCount, `features` is always `[]` and only aggregate counts are safe to show. */
  suppressed?: boolean;
}

const VALID_STATUSES: readonly PublicConcernStatus[] = ["open", "in-progress", "resolved"];

/** Defense-in-depth: an unrecognized status value is never rendered as a real pin (mirrors the
 *  server's own "an unrecognized status is never public" posture in shared.PublicConcernStatus). */
function isPublicConcernStatus(s: string): s is PublicConcernStatus {
  return (VALID_STATUSES as readonly string[]).includes(s);
}

/** Maps one wire GeoJSON Feature to the flat PublicConcernPin view model. Returns null for a
 *  malformed/unrecognized-status feature rather than throwing — a single bad row must never break
 *  the whole map/list. */
function mapFeature(f: WireConcernFeature): PublicConcernPin | null {
  if (!isPublicConcernStatus(f.properties.status)) return null;
  const [lng, lat] = f.geometry.coordinates;
  return {
    ref: f.properties.ref,
    category: f.properties.category,
    categoryId: f.properties.categoryId,
    type: f.properties.type,
    status: f.properties.status,
    lat,
    lng,
    location: f.properties.location,
    dateReported: f.properties.dateReported,
    dateResolved: f.properties.dateResolved ?? null,
  };
}

/** Pure mapping function (wire -> view model) — exported separately from the live fetch so it's
 *  unit-testable without a network/mock-server round-trip (apiMapping.test.ts convention). */
export function wireToConcernsView(res: WireConcernsResponse): PublicConcernsView {
  return {
    pins: (res.features ?? []).map(mapFeature).filter((p): p is PublicConcernPin => p !== null),
    summary: {
      reported: res.summary?.reported ?? 0,
      resolved: res.summary?.resolved ?? 0,
    },
    suppressed: res.suppressed === true,
  };
}

/** GET /api/public/concerns?category=&status=&from=&to= — see types.ts's PublicConcernsView doc
 *  comment for the k-anonymity-suppression contract the UI must handle gracefully. */
export async function listPublicConcernsLive(
  filter: PublicConcernsFilter = {},
): Promise<PublicConcernsView> {
  const res = await apiFetch<WireConcernsResponse>("/public/concerns", {
    query: {
      category: filter.category,
      status: filter.status,
      from: filter.from,
      to: filter.to,
    },
  });
  return wireToConcernsView(res);
}
